import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { GoalProvider } from "./contexts/GoalContext";
import { OKRProvider } from "./contexts/OKRContext";
import { PerformanceProvider } from "./contexts/PerformanceContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginForm from "./components/LoginForm";
import RegisterForm from "./components/RegisterForm";
import Dashboard from "./components/Dashboard";
import EnhancedDashboard from "./components/EnhancedDashboard";
import Profile from "./components/Profile";
import GoalList from "./components/GoalList";
import OKRList from "./components/OKRList";
import PerformanceReviewList from "./components/PerformanceReviewList";
import AdvancedAnalytics from "./components/AdvancedAnalytics";
import ReviewCycleManager from "./components/ReviewCycleManager";
import PerformanceAnalytics from "./components/PerformanceAnalytics";
import LoadingSpinner from "./components/LoadingSpinner";

// Component to handle authentication-based routing
const AuthRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={user ? <Navigate to="/dashboard" replace /> : <LoginForm />} 
      />
      <Route 
        path="/register" 
        element={user ? <Navigate to="/dashboard" replace /> : <RegisterForm />} 
      />
      
      {/* Protected routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <EnhancedDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/classic-dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/goals" 
        element={
          <ProtectedRoute>
            <GoalList />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/okrs" 
        element={
          <ProtectedRoute>
            <OKRList />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/advanced-analytics" 
        element={
          <ProtectedRoute>
            <AdvancedAnalytics />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/performance-reviews" 
        element={
          <ProtectedRoute>
            <PerformanceReviewList />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/review-cycles" 
        element={
          <ProtectedRoute requiredRoles={['hr', 'admin']}>
            <ReviewCycleManager />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/performance-analytics" 
        element={
          <ProtectedRoute requiredRoles={['hr', 'admin', 'manager']}>
            <PerformanceAnalytics />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } 
      />
      
      {/* Default redirect */}
      <Route 
        path="/" 
        element={
          user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
        } 
      />
      
      {/* Catch all route */}
      <Route 
        path="*" 
        element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h2>
                <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
                <button
                  onClick={() => window.location.href = user ? '/dashboard' : '/login'}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-200"
                >
                  {user ? 'Go to Dashboard' : 'Go to Login'}
                </button>
              </div>
            </div>
          </div>
        } 
      />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <GoalProvider>
              <OKRProvider>
                <PerformanceProvider>
                  <AuthRoutes />
                </PerformanceProvider>
              </OKRProvider>
            </GoalProvider>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;